import { useState, useMemo, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnDef,
  SortingState,
  RowSelectionState,
  OnChangeFn,
  FilterFn,
  flexRender,
} from '@tanstack/react-table';
import { DataTableVisual } from './data-table-visual';
import { DataTableColumn } from './data-table.types';

/**
 * Fuzzy word match: splits query into words and checks that every word
 * appears as a substring in the text (case-insensitive).
 * e.g. "apac display" matches "APAC_Display_Retargeting"
 */
const fuzzyMatch = (text: string, query: string): boolean => {
  const lowerText = text.toLowerCase();
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  return words.every(word => lowerText.includes(word));
};

/**
 * TanStack Table filter function that performs fuzzy word matching
 * on each cell value in the row.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fuzzyFilterFn: FilterFn<any> = (row, columnId, filterValue) => {
  const cellValue = row.getValue(columnId);
  if (cellValue === null || cellValue === undefined) return false;
  return fuzzyMatch(String(cellValue), String(filterValue));
};

export interface ClientDataTableProps<TData> {
  /** TanStack Table column definitions */
  columns: ColumnDef<TData>[];
  /** Raw data array */
  data: TData[];

  // Visual styling
  /** Visual appearance style */
  appearance?: 'default' | 'minimal';

  // Search
  /** Enable search input */
  enableSearch?: boolean;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Enable fuzzy word matching where all query words must be present as substrings. */
  fuzzySearch?: boolean;
  /** Columns to exclude from global filter */
  excludedGlobalFilterColumns?: string[];

  // Pagination
  /** Enable pagination controls */
  enablePagination?: boolean;
  /** Items per page */
  pageSize?: number;
  /** Initial page (0-indexed) */
  initialPage?: number;

  // Sorting
  /** Initial sorting state */
  initialSorting?: SortingState;

  // Row Selection
  /** Enable row selection checkboxes */
  enableRowSelection?: boolean;
  /** Function to get unique row ID - CRITICAL for stable row selection when sorting/filtering */
  getRowId?: (row: TData, index: number) => string;
  /** Callback when selection changes */
  onSelectionChange?: (selectedRows: TData[]) => void;
  /** Controlled row selection state */
  rowSelection?: RowSelectionState;
  /** Callback for row selection state changes */
  onRowSelectionChange?: (selection: RowSelectionState) => void;

  // Scrolling
  /** Enable vertical scrolling with fixed height */
  scrollable?: boolean;
  /** Height for scrollable container */
  height?: string;
  /** Enable horizontal scrolling */
  horizontalScroll?: boolean;

  // Export
  /** Show download/copy export actions above the table. Default: true */
  enableExport?: boolean;
  /** Filename for exported files (without extension). Default: 'table-data' */
  exportFilename?: string;

  // Row Click
  /** Callback when a data row is clicked. Adds cursor-pointer styling to clickable rows. */
  onRowClick?: (row: TData, rowIndex: number) => void;

  // Border
  /** Show rounded border around the table wrapper. Default: true */
  bordered?: boolean;

  // Empty/Loading states
  /** Empty state message */
  emptyText?: string;

  /** Additional CSS classes */
  className?: string;
}

/**
 * ClientDataTable - Client-side data operations with TanStack Table built-in
 *
 * @example
 * ```tsx
 * <ClientDataTable
 *   columns={columns}
 *   data={myData}
 *   enableSearch
 *   enablePagination
 *   pageSize={10}
 * />
 * ```
 *
 * @internal - Use DataTable with variant="client" instead
 */
export function ClientDataTable<TData>({
  columns,
  data,
  appearance = 'default',
  enableSearch = false,
  searchPlaceholder,
  fuzzySearch = false,
  excludedGlobalFilterColumns = [],
  enablePagination = false,
  pageSize = 10,
  initialPage = 0,
  initialSorting = [],
  enableRowSelection = false,
  getRowId,
  onSelectionChange,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  onRowClick,
  enableExport,
  exportFilename,
  bordered,
  scrollable = false,
  height,
  horizontalScroll = false,
  emptyText,
  className,
}: ClientDataTableProps<TData>) {
  // State
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [globalFilter, setGlobalFilter] = useState('');
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});

  const rowSelection = controlledRowSelection ?? internalRowSelection;

  // TanStack Table expects OnChangeFn which can receive an updater function or a value
  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updaterOrValue) => {
    const handler = onRowSelectionChange ?? setInternalRowSelection;
    if (typeof updaterOrValue === 'function') {
      handler(updaterOrValue(rowSelection));
    } else {
      handler(updaterOrValue);
    }
  };

  // Modify columns to disable global filtering for excluded columns
  const enhancedColumns = useMemo(() => {
    return columns.map((col) => {
      if ('accessorKey' in col && excludedGlobalFilterColumns.includes(col.accessorKey as string)) {
        return { ...col, enableGlobalFilter: false };
      }
      return col;
    });
  }, [columns, excludedGlobalFilterColumns]);

  // Setup TanStack Table
  const table = useReactTable<TData>({
    data,
    columns: enhancedColumns,
    state: {
      sorting,
      globalFilter,
      rowSelection,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    ...(fuzzySearch ? { globalFilterFn: fuzzyFilterFn } : {}),
    onRowSelectionChange: handleRowSelectionChange,
    enableRowSelection,
    // CRITICAL: Use provided getRowId for stable row selection during sort/filter
    getRowId: getRowId || ((_, index) => index.toString()),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(enablePagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    initialState: {
      pagination: {
        pageSize,
        pageIndex: initialPage,
      },
    },
  });

  // Transform TanStack columns to DataTable visual format
  const visualColumns: DataTableColumn<TData>[] = useMemo(() => {
    const headerGroup = table.getHeaderGroups()[0];
    if (!headerGroup) return [];

    return headerGroup.headers.map((header) => ({
      id: header.id,
      header: flexRender(header.column.columnDef.header, header.getContext()),
      cell: (_row, rowIndex) => {
        const cell = table.getRowModel().rows[rowIndex]
          ?.getVisibleCells()
          .find((c) => c.column.id === header.id);
        return cell ? flexRender(cell.column.columnDef.cell, cell.getContext()) : null;
      },
      sortable: header.column.getCanSort(),
      // Preserve column sizing if specified
      ...(header.column.columnDef.size && { width: header.column.columnDef.size }),
      ...(header.column.columnDef.minSize && { minWidth: header.column.columnDef.minSize }),
      ...(header.column.columnDef.maxSize && { maxWidth: header.column.columnDef.maxSize }),
    }));
  }, [table]);

  // Get processed rows — no useMemo on `table` since useReactTable returns a
  // stable object reference; memoizing on it would produce stale rows when data changes.
  const visualRows = table.getRowModel().rows.map((row) => row.original);

  // Handle sorting
  const handleSort = (columnId: string) => {
    const currentSort = sorting[0];
    if (currentSort?.id === columnId) {
      setSorting([{ id: columnId, desc: !currentSort.desc }]);
    } else {
      setSorting([{ id: columnId, desc: false }]);
    }
  };

  // Handle selection changes
  const rowSelectionString = JSON.stringify(rowSelection);
  useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
      onSelectionChange(selectedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelectionString, onSelectionChange]);

  // Transform row selection to Set for visual component.
  // Keys are kept as strings to match getRowId return type (Object.keys always returns strings).
  const selectedRowsSet = useMemo(() => {
    const selected = new Set<string | number>();
    Object.keys(rowSelection).forEach((key) => {
      if (rowSelection[key]) {
        selected.add(key);
      }
    });
    return selected;
  }, [rowSelection]);

  const handleRowSelect = (rowId: string | number, selected: boolean) => {
    const newSelection = {
      ...rowSelection,
      [rowId]: selected,
    };
    handleRowSelectionChange(newSelection);
  };

  const handleSelectAll = (selected: boolean) => {
    table.toggleAllPageRowsSelected(selected);
  };

  return (
    <DataTableVisual
      columns={visualColumns}
      rows={visualRows}
      variant={appearance}
      enableSearch={enableSearch}
      searchValue={globalFilter}
      onSearchChange={setGlobalFilter}
      searchPlaceholder={searchPlaceholder}
      sortedColumn={sorting[0]?.id}
      sortDirection={sorting[0]?.desc ? 'desc' : 'asc'}
      onSort={handleSort}
      enablePagination={enablePagination}
      currentPage={table.getState().pagination.pageIndex + 1}
      totalPages={table.getPageCount()}
      onPageChange={(page) => table.setPageIndex(page - 1)}
      enableRowSelection={enableRowSelection}
      selectedRows={selectedRowsSet}
      onRowSelect={handleRowSelect}
      onSelectAll={handleSelectAll}
      getRowId={getRowId || ((_, index) => String(index))}
      onRowClick={onRowClick}
      enableExport={enableExport}
      exportFilename={exportFilename}
      bordered={bordered}
      scrollable={scrollable}
      height={height}
      horizontalScroll={horizontalScroll}
      emptyText={emptyText}
      className={className}
    />
  );
}
